import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { callGoogleAdsProxy, GOOGLE_ADS_API_VERSION_PATH } from "../services/google-ads-proxy-client";

import type { IGoogleAdsAccountOption } from "./use-google-ads-accounts";
import { useGoogleAdsConnection } from "./use-google-ads-oauth";

export interface IGoogleAdsCampaignListItem {
    id: string;
    name: string;
    status: string;
}

interface IGoogleAdsCampaignSearchResponse {
    results?: { campaign?: { id?: string; name?: string; status?: string } }[];
}

const CAMPAIGNS_QUERY = "SELECT campaign.id, campaign.name, campaign.status FROM campaign ORDER BY campaign.id";

const fetchGoogleAdsCampaigns = async (account: IGoogleAdsAccountOption): Promise<IGoogleAdsCampaignListItem[]> => {
    const campaignsResponse = await callGoogleAdsProxy<IGoogleAdsCampaignSearchResponse>({
        method: "POST",
        path: `${GOOGLE_ADS_API_VERSION_PATH}/customers/${account.id}/googleAds:search`,
        body: { query: CAMPAIGNS_QUERY },
        loginCustomerId: account.loginCustomerId,
    });

    return (campaignsResponse.results ?? [])
        .map((result) => result.campaign)
        .filter((campaign): campaign is { id: string; name?: string; status?: string } => Boolean(campaign?.id))
        .map((campaign) => ({
            id: campaign.id,
            name: campaign.name ?? "",
            status: campaign.status ?? "",
        }));
};

export const useGoogleAdsCampaigns = (
    account: IGoogleAdsAccountOption | null,
): UseQueryResult<IGoogleAdsCampaignListItem[]> => {
    const { isConnected, connectionId } = useGoogleAdsConnection();

    return useQuery({
        queryKey: ["googleAdsCampaigns", connectionId, account?.id],
        queryFn: () => fetchGoogleAdsCampaigns(account as IGoogleAdsAccountOption),
        enabled: isConnected && Boolean(account),
        retry: false,
    });
};
